import { Badge, LegacyCard, ExceptionList, Layout, Page, LegacyStack, Text, Link } from '@shopify/polaris';
import { HorizontalDotsMinor } from '@shopify/polaris-icons';
import { useEffect } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import fillZeros from '../../../helpers/fillZeros';
import { getDeliveryById, getDeliveryStockById, putDeliveryDeposit } from '../../../services/deliveriesService';
import SelectDeposit from '../../UI/Deliveries/SelectDeposit';
import ToastContext from '../../../context/ToastContext';
import { useContext } from 'react';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import DeliveryStockTableClosed from '../../UI/Tables/DeliveryStockTableClosed';
import ViewDeliveriesPrimaryActions from '../../UI/Deliveries/ViewDeliveriesPrimaryActions';
import AddStockCard from '../../UI/Deliveries/AddStockCard';
import UnknownEansNotice from '../../UI/Deliveries/UnknownEansNotice';

const ViewDelivery = () => {
  const { id } = useParams();
  const [delivery, setDelivery] = useState(null);
  const [stock, setStock] = useState(null);
  const [, dispatchToast] = useContext(ToastContext);

  const returnMatch = window.location.search.match(/\??r=(.+)&?/);
  const returnUrl = returnMatch && returnMatch.length > 1 ? window.atob(returnMatch[1]) : '/deliveries';

  const loadDelivery = () =>
    getDeliveryById(id)
      .then((r) => {
        setDelivery(r.data);
      })
      .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));

  const loadStock = (args = { page: 1, limit: 10 }) =>
    getDeliveryStockById(id, args)
      .then((r) => {
        setStock(r.data);
      })
      .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));

  useEffect(() => {
    loadDelivery();
    loadStock();
    // eslint-disable-next-line
  }, []);

  const handleChangeDeposit = (deposit) => {
    putDeliveryDeposit(id, deposit)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        loadDelivery();
      })
      .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));
  };

  const handleToggleState = ({ newState }) => {
    delivery.status = newState === 'OPEN' ? 'OPEN' : 'CLOSED';
  };

  const handleAcceptAll = () => {
    loadDelivery();
    setStock(null);
    loadStock();
  };

  const handleStockAdded = (product) => {
    setStock({
      ...stock,
      data: [product],
      update: true,
    });
  };

  const TableComponentClosed = (args) => <DeliveryStockTableClosed {...args} />;

  return <>
    {delivery && (
      <Page
        title={delivery.name}
        titleMetadata={
          delivery.deposit &&
          delivery.status === 'OPEN' && (
            <ExceptionList
              items={[
                {
                  icon: HorizontalDotsMinor,
                  status: 'warning',
                  title: 'Recepción abierta',
                },
              ]}
            />
          )
        }
        subtitle={
          <span className="block">
            <span className="inline-block">{`#${fillZeros(delivery.id, 4)}`}</span>
            {delivery.deposit && (
              <span className="inline-block ml-4">
                Recepción en{' '}
                <Link
                  monochrome
                  url={`/deposits/${delivery.deposit.id}?r=${window.btoa(
                    window.location.pathname + window.location.search,
                  )}`}
                >
                  {delivery.deposit.name}
                </Link>
              </span>
            )}
            <span className="inline-block ml-4">
              {delivery.status === 'CLOSED' && <Badge status="new">Cerrado</Badge>}
            </span>
          </span>
        }
        backAction={{ content: 'Ver entregas', url: returnUrl }}
        primaryAction={
          <ViewDeliveriesPrimaryActions
            delivery={delivery}
            status={delivery.status}
            id={delivery.id}
            onToggleState={handleToggleState}
            onAcceptAll={handleAcceptAll}
          />
        }
        divider
      >
        <Layout>
          {!delivery.deposit && (
            <Layout.Section>
              <LegacyCard>
                <LegacyCard.Section>
                  <SelectDeposit delivery={delivery} onSubmit={handleChangeDeposit} />
                </LegacyCard.Section>
              </LegacyCard>
            </Layout.Section>
          )}

          {stock && <UnknownEansNotice unknownEans={stock.unknownEans} />}

          {delivery.deposit && (
            <>
              <DeliveryNumbers delivery={delivery} />

              {delivery.status === 'OPEN' && (
                <Layout.Section>
                  <AddStockCard delivery={delivery} onStockAdded={handleStockAdded} />
                </Layout.Section>
              )}

              <Layout.Section>
                {stock && (
                  <ProductsWrapper
                    data={stock}
                    TableComponent={TableComponentClosed}
                    loadPage={() => loadStock({ page: stock.metadata.page + 1, limit: stock.metadata.limit })}
                  />
                )}
              </Layout.Section>
            </>
          )}
        </Layout>
      </Page>
    )}
  </>;
};

const DeliveryNumbers = ({ delivery }) => {
  return (
    <Layout.Section>
      <LegacyCard>
        <LegacyCard.Section>
          <LegacyStack distribution="fill">
            <LegacyStack.Item>
              <Text variant="headingXs" weight="semibold" as="h3">
                Enviado
              </Text>
              <Text variant="heading3xl">{delivery.stockQtyExpected} Uds.</Text>
            </LegacyStack.Item>
            <LegacyStack.Item>
              <Text variant="headingXs" weight="semibold">
                Aceptado
              </Text>
              <Text variant="heading3xl">{delivery.stockQty} Uds.</Text>
            </LegacyStack.Item>
            <LegacyStack.Item>
              <Text variant="headingXs" weight="semibold">
                {delivery.stockQtyExpected - delivery.stockQty === 0
                  ? 'Diferencia'
                  : delivery.stockQtyExpected - delivery.stockQty > 0
                  ? 'Faltan'
                  : 'Sobran'}
              </Text>
              <Text variant="heading3xl">{Math.abs(delivery.stockQtyExpected - delivery.stockQty)} Uds.</Text>
            </LegacyStack.Item>
          </LegacyStack>
        </LegacyCard.Section>
      </LegacyCard>
    </Layout.Section>
  );
};

export default ViewDelivery;
